<template>
  <div class="signup-page bg-background min-h-screen d-flex flex-column">

    <!-- HERO -->
    <v-container fluid class="hero-section py-16 text-center">
      <h1 class="hero-title">
        Sign In
      </h1>
    </v-container>

    <!-- Auth Panel (email-first flow) -->
    <v-container
      fluid
      class="flex-grow-1 d-flex align-center justify-center py-12"
    >
      <v-sheet elevation="8" rounded="xl" class="auth-shell">
        <v-row class="h-100 g-0" no-gutters>
          <!-- RIGHT: Auth Pane -->
          <!-- 👇 h-100 so column fills the sheet -->
          <v-col cols="12" md="12" class="d-flex h-100">
            <div class="auth-pane">
              <div class="auth-pane-inner">
                <h1 class="auth-title">
                  {{
                    phase === 'email'
                      ? 'Welcome'
                      : phase === 'login'
                        ? 'Enter your password'
                        : 'Create Account'
                  }}
                </h1>

                <div class="text-body-1 text-grey-darken-1 mb-6">
                  <span v-if="phase === 'email'">
                    Enter your email to continue.
                  </span>
                  <span v-else-if="phase === 'login'">
                    Using <strong>{{ email }}</strong>
                  </span>
                  <span v-else>
                    We’ll create an account for <strong>{{ email }}</strong>.
                  </span>
                </div>

                <!-- ERROR MESSAGE -->
                <v-alert
                  v-if="errorMessage"
                  type="error"
                  variant="tonal"
                  class="mb-4"
                >
                  {{ errorMessage }}
                </v-alert>

                <!-- Stable body with transition & min-height -->
                <div class="auth-form-body">
                  <transition name="fade-slide" mode="out-in">
                    <!-- PHASE 1: EMAIL ONLY -->
                    <v-form
                      v-if="phase === 'email'"
                      key="email"
                      @submit.prevent="handleEmailContinue"
                    >
                      <v-text-field
                        v-model="email"
                        label="Email"
                        type="email"
                        variant="outlined"
                        density="comfortable"
                        class="mb-4"
                        prepend-inner-icon="mdi-email-outline"
                        autocomplete="email"
                        required
                      />

                      <v-btn
                        type="submit"
                        color="primary"
                        block
                        class="auth-cta"
                        :loading="loading"
                      >
                        CONTINUE
                      </v-btn>
                    </v-form>

                    <!-- PHASE 2: LOGIN (ACCOUNT EXISTS) -->
                    <v-form
                      v-else-if="phase === 'login'"
                      key="login"
                      @submit.prevent="handleLogin"
                    >
                      <v-text-field
                        v-model="email"
                        label="Email"
                        type="email"
                        variant="outlined"
                        density="comfortable"
                        class="mb-3"
                        prepend-inner-icon="mdi-email-outline"
                        disabled
                      />

                      <v-text-field
                        v-model="loginPassword"
                        label="Password"
                        type="password"
                        variant="outlined"
                        density="comfortable"
                        class="mb-1"
                        prepend-inner-icon="mdi-lock-outline"
                        autocomplete="current-password"
                        required
                      />

                      <div class="d-flex justify-end mt-1 mb-4">
                        <a class="auth-link" href="/forgot-password">
                          Forgot your password?
                        </a>
                      </div>

                      <v-btn
                        type="submit"
                        color="primary"
                        block
                        class="auth-cta"
                        :loading="loading"
                      >
                        LOG IN
                      </v-btn>

                      <div class="text-body-2 text-grey-darken-1 mt-4 text-center">
                        <a class="auth-link" href="#" @click.prevent="goBackToEmail">
                          Use a different email
                        </a>
                      </div>
                    </v-form>

                    <!-- PHASE 3: SIGN UP (NO ACCOUNT YET) -->
                    <v-form
                      v-else
                      key="register"
                      @submit.prevent="handleRegister"
                    >
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.firstName"
                            label="First Name"
                            variant="outlined"
                            density="comfortable"
                            required
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.lastName"
                            label="Last Name"
                            variant="outlined"
                            density="comfortable"
                            required
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.email"
                            label="Email"
                            type="email"
                            variant="outlined"
                            density="comfortable"
                            prepend-inner-icon="mdi-email-outline"
                            readonly
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.phone"
                            label="Phone"
                            variant="outlined"
                            density="comfortable"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.password"
                            label="Password"
                            type="password"
                            variant="outlined"
                            density="comfortable"
                            required
                          />
                          <ul class="hint">
                            <li :class="passwordHasUppercase ? 'ok' : 'bad'">
                              Must contain an uppercase letter
                            </li>
                            <li :class="passwordHasLength ? 'ok' : 'bad'">
                              Must be at least 6 characters
                            </li>
                          </ul>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.confirmPassword"
                            label="Confirm Password"
                            type="password"
                            variant="outlined"
                            density="comfortable"
                            required
                          />
                          <ul class="hint">
                            <li :class="passwordsMatch ? 'ok' : 'bad'">
                              Passwords match
                            </li>
                          </ul>
                        </v-col>
                      </v-row>

                      <v-checkbox
                        v-model="form.agree"
                        label="I agree to the terms and conditions"
                        color="primary"
                        density="comfortable"
                        hide-details
                        true-icon="mdi-check-bold"
                        false-icon="mdi-checkbox-blank-outline"
                      />

                      <v-btn
                        type="submit"
                        color="primary"
                        block
                        class="auth-cta"
                        :loading="loading"
                      >
                        CREATE ACCOUNT
                      </v-btn>

                      <div class="text-body-2 text-grey-darken-1 mt-4 text-center">
                        <a class="auth-link" href="#" @click.prevent="goBackToEmail">
                          Use a different email
                        </a>
                      </div>
                    </v-form>
                  </transition>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-sheet>
    </v-container>

    <!-- Travel Agents Section (NOT a card) -->
    <v-container fluid class="py-16 bg-white text-center">
      <v-row justify="center">
        <v-col cols="12" md="8">
          <h2 class="text-h3 font-weight-bold mb-2">For Travel Professionals</h2>
          <p class="mb-4" style="font-size: 1.85rem; line-height: 1.8;">
            Have Extra Space to Fill?
          </p>
          <p class="mb-6" style="font-size: 1.25rem; line-height: 1.8;">
            We help you list, connect, and convert—without the extra hassle. Tap into our network of eager
            travelers and turn your unused space into value.
          </p>
          <router-link to="/agent">
            <v-btn color="primary" size="large" class="px-10">Learn More</v-btn>
          </router-link>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { supabase } from '@/supabaseClient'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

const router = useRouter()
const store = useStore()

const phase = ref('email')
const email = ref('')
const loginPassword = ref('')

const snack = reactive({ show: false, color: 'success', msg: '' })
const errorMessage = ref('')
const loading = ref(false)

const form = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  agree: false
})

const passwordHasUppercase = computed(() => /[A-Z]/.test(form.value.password))
const passwordHasLength = computed(() => form.value.password.length >= 6)
const passwordsMatch = computed(() => form.value.password === form.value.confirmPassword)

function resetErrors() {
  errorMessage.value = ''
}

function goBackToEmail() {
  phase.value = 'email'
  loginPassword.value = ''
  resetErrors()
}

async function checkEmailExists(targetEmail) {
  const { data, error } = await supabase.functions.invoke('check-email', {
    body: { email: targetEmail }
  })

  if (error) throw error

  if (data?.error) {
    console.warn('check-email function error:', data.error)
    return null
  }

  return !!data?.exists
}

async function handleEmailContinue() {
  resetErrors()

  if (!email.value) {
    errorMessage.value = 'Please enter your email.'
    return
  }

  const simpleEmailRegex = /\S+@\S+\.\S+/
  if (!simpleEmailRegex.test(email.value)) {
    errorMessage.value = 'Please enter a valid email address.'
    return
  }

  loading.value = true
  try {
    const exists = await checkEmailExists(email.value)

    if (exists) {
      phase.value = 'login'
      loginPassword.value = ''
    } else {
      phase.value = 'register'
      form.value.email = email.value
    }
  } catch (err) {
    errorMessage.value = 'Unable to continue. Please try again.'
  } finally {
    loading.value = false
  }
}

async function handleLogin() {
  resetErrors()
  if (!email.value || !loginPassword.value) {
    errorMessage.value = 'Please enter your password.'
    return
  }

  loading.value = true
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.value,
      password: loginPassword.value
    })

    if (error) {
      errorMessage.value = 'Email or password is incorrect.'
      snack.msg = 'Login failed.'
      snack.color = 'red'
      snack.show = true
      return
    }

    const uid = data?.user?.id
    if (uid) {
      const { data: profile } = await supabase
        .from('users')
        .select('*')
        .eq('uid', uid)
        .single()

      store.commit('setUser', profile || data.user)
    }

    await router.replace('/')
  } catch (err) {
    errorMessage.value = 'Something went wrong. Please try again.'
  } finally {
    loading.value = false
  }
}

async function handleRegister() {
  resetErrors()
  form.value.email = email.value || form.value.email

  if (
    !form.value.firstName ||
    !form.value.lastName ||
    !form.value.email ||
    !form.value.phone ||
    !form.value.agree ||
    !passwordHasUppercase.value ||
    !passwordHasLength.value ||
    !passwordsMatch.value
  ) {
    errorMessage.value = 'Please complete all fields and agree to terms.'
    return
  }

  loading.value = true
  try {
    const { data: signupData, error: signupError } = await supabase.auth.signUp({
      email: form.value.email,
      password: form.value.password,
      options: {
        data: {
          firstName: form.value.firstName,
          lastName: form.value.lastName,
          phone: form.value.phone,
          isAdmin: false
        }
      }
    })

    if (signupError) throw signupError

    const user = signupData.user
    if (user?.id) {
      await supabase.from('users').insert([
        {
          uid: user.id,
          email: form.value.email,
          first_name: form.value.firstName,
          last_name: form.value.lastName,
          phone: form.value.phone,
          created_at: new Date().toISOString()
        }
      ])
    }

    if (!signupData.session) {
      snack.msg = 'Signed up successfully! Please check your email for verification.'
      snack.color = 'success'
      snack.show = true
      await router.replace('/')
      return
    }

    store.commit('setUser', { ...(user || {}), isAdmin: false })
    await router.replace('/')
  } catch (err) {
    const msg = err?.message || ''
    if (msg.includes('already registered')) {
      errorMessage.value = 'This email is already registered. Please log in.'
      phase.value = 'login'
    } else if (msg.includes('invalid email')) {
      errorMessage.value = 'Please enter a valid email address.'
    } else if (msg.includes('Password')) {
      errorMessage.value = 'Password should be at least 6 characters and include an uppercase letter.'
    } else {
      errorMessage.value = 'Something went wrong. Please try again.'
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.min-h-screen {
  min-height: 100vh;
}

/* Card */
.auth-shell {
  width: min(800px, 96vw);
  background: #fff;
  border-radius: 20px;
  display: flex;
}

/* Column wrapper fills height */
.auth-pane {
  flex: 1;
  display: flex;
}

/* Inner content centered vertically, but width constrained */
.auth-pane-inner {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 44px 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;  /* vertical centering */
}

/* Stable area for forms */
.auth-form-body {
  margin-top: 8px;
  min-height: 220px;
  display: flex;
  flex-direction: column;
}

/* Title */
.auth-title {
  font-size: 44px;
  line-height: 1.1;
  margin-bottom: 8px;
  font-weight: 800;
}

/* Button */
.auth-cta {
  height: 44px;
  letter-spacing: 0.5px;
  font-weight: 700;
  border-radius: 8px;
}

/* Links */
.auth-link {
  color: var(--color-secondary);
  text-decoration: none;
}
.auth-link:hover {
  text-decoration: underline;
}

/* Password hints */
.hint {
  margin: 6px 0 0;
  padding-left: 0;
  list-style: none;
}
.hint li {
  font-size: 0.78rem;
}
.hint .ok {
  color: #2e7d32;
}
.hint .bad {
  color: #c62828;
}

/* Transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* Responsive */
@media (max-width: 960px) {
  .auth-shell {
    min-height: 460px;
  }
  .auth-pane-inner {
    padding: 28px 22px;
  }
  .auth-title {
    font-size: 32px;
  }
}
</style>
